/**
 * AssignToAccountsDialog
 *
 * Used by both the Inventory page (bulk assign N numbers to M tenants) and the
 * per-account Phone Numbers tab (assign 1+ existing numbers to this tenant).
 *
 * Picks the account list from /api/super-admin/accounts and lets the operator
 * multi-select tenants. Optionally one can be marked as the default sender for
 * those tenants — applied per assigned (number, tenant) pair on the server.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Phone, X, Check, Star } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@respectly/ui';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { InventoryItem } from '../../stores/useNumberInventoryStore';

interface AccountListItem {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** The numbers being assigned. When > 1, the dialog is in "bulk" mode. */
  targets: InventoryItem[];
  /** Tenant ids to hide from the picker (already assigned, etc.). */
  excludeTenantIds: Set<string>;
  isSubmitting: boolean;
  onSubmit: (params: {
    tenantIds: string[];
    setDefaultForTenantId?: string | null;
  }) => Promise<boolean> | boolean;
}

function formatE164(num: string): string {
  const m = num.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return num;
}

export function AssignToAccountsDialog({
  open,
  onClose,
  targets,
  excludeTenantIds,
  isSubmitting,
  onSubmit,
}: Props) {
  const [accounts, setAccounts] = useState<AccountListItem[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedTenantIds, setSelectedTenantIds] = useState<Set<string>>(new Set());
  const [defaultTenantId, setDefaultTenantId] = useState<string | null>(null);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedTenantIds(new Set());
    setDefaultTenantId(null);
  }, [open]);

  // Fetch the (paged) account list once when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setIsLoadingAccounts(true);
      try {
        const api = getApiClient();
        const params = new URLSearchParams({ page: '1', limit: '500' });
        const result = await api.get<{ data: AccountListItem[] }>(
          `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.LIST}?${params.toString()}`,
        );
        if (!cancelled) setAccounts(result.data ?? []);
      } catch {
        if (!cancelled) setAccounts([]);
      } finally {
        if (!cancelled) setIsLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts
      .filter((a) => !excludeTenantIds.has(a.id))
      .filter((a) =>
        q
          ? a.name.toLowerCase().includes(q) ||
            a.slug.toLowerCase().includes(q) ||
            (a.ownerEmail?.toLowerCase().includes(q) ?? false)
          : true,
      );
  }, [accounts, search, excludeTenantIds]);

  const toggleTenant = (id: string) => {
    setSelectedTenantIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (defaultTenantId === id) setDefaultTenantId(null);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedTenantIds.size === 0) return;
    await onSubmit({
      tenantIds: Array.from(selectedTenantIds),
      setDefaultForTenantId: defaultTenantId,
    });
  };

  const targetCount = targets.length;
  const tenantCount = selectedTenantIds.size;
  const willCreate = targetCount * tenantCount;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-slate-500" />
            Assign {targetCount === 1 ? 'number' : `${targetCount} numbers`} to accounts
          </DialogTitle>
        </DialogHeader>

        {/* Selected numbers summary */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-900/40">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
            {targetCount === 1 ? 'Number' : 'Numbers'}
          </p>
          <ul className="mt-1.5 flex flex-wrap gap-1.5">
            {targets.map((t) => (
              <li
                key={t.telnyx.id}
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 font-mono text-xs tabular-nums text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {formatE164(t.telnyx.phoneNumber)}
              </li>
            ))}
          </ul>
        </div>

        {/* Account picker */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search accounts by name, slug, or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          <div className="max-h-[280px] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-800">
            {isLoadingAccounts ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              </div>
            ) : filteredAccounts.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                No matching accounts.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredAccounts.map((acct) => {
                  const checked = selectedTenantIds.has(acct.id);
                  const isDefault = defaultTenantId === acct.id;
                  return (
                    <li
                      key={acct.id}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                        checked
                          ? 'bg-sky-50/60 dark:bg-sky-950/20'
                          : 'hover:bg-slate-50/60 dark:hover:bg-slate-900/40',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleTenant(acct.id)}
                        className={cn(
                          'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                          checked
                            ? 'border-sky-500 bg-sky-500 text-white'
                            : 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900',
                        )}
                      >
                        {checked && <Check className="h-3 w-3" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-slate-900 dark:text-white">
                          {acct.name}
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-500">
                          <span className="truncate">{acct.slug}</span>
                          {acct.ownerEmail && (
                            <>
                              <span className="text-slate-300 dark:text-slate-600">·</span>
                              <span className="truncate font-mono text-[11px] text-slate-500">
                                {acct.ownerEmail}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      {checked && (
                        <button
                          type="button"
                          title={
                            isDefault
                              ? 'Default sender for this account'
                              : 'Make default sender for this account'
                          }
                          onClick={() =>
                            setDefaultTenantId((prev) => (prev === acct.id ? null : acct.id))
                          }
                          className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-md',
                            isDefault
                              ? 'text-amber-500'
                              : 'text-slate-300 hover:text-amber-400',
                          )}
                        >
                          <Star
                            className={cn('h-4 w-4', isDefault && 'fill-current')}
                          />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {tenantCount > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900/40 dark:text-slate-400">
              {targetCount === 1 && tenantCount === 1
                ? 'Will create 1 assignment.'
                : `Will create ${willCreate} assignment${willCreate === 1 ? '' : 's'} (${targetCount} × ${tenantCount}).`}
              {defaultTenantId &&
                ` Marked as default sender for the starred account on each number.`}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            <X className="mr-1.5 h-4 w-4" />
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={tenantCount === 0 || isSubmitting}
            className="bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
          >
            {isSubmitting ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-1.5 h-4 w-4" />
            )}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
